import React from "react";
import { Select } from "Components/Base/Inputs/Select";
import { MenuItem } from "Components/Base/Inputs/MenuItem";
import { InputLabel } from "Components/Base/Inputs/InputLabel";
import { FormControl } from "Components/Base/Inputs/FormControl";

/**
 * FormControlSelect component renders a select dropdown with label and options.
 *
 * @param {Object} props - Component props
 * @param {string} props.name - The name and label for the select input
 * @param {Array<{ value: string|number, label: string }>} props.options - Array of option objects for the select
 * @param {string|number} props.value - The currently selected value
 * @param {function} props.onChange - Callback function called with the new value when selection changes
 * @param {string} [props.className] - Additional class names for styling
 * @param {boolean} [props.fullWidth=false] - Whether the select should take full width
 * @param {boolean} [props.displayLabel=true] - Whether to display the label (otherwise visually hidden)
 * @param {Object} [props.sx] - Style overrides for the component
 * @param {function} [props.languageContext] - Function for translating option labels
 * @param {boolean} [props.translateOptions=false] - Whether to translate option labels using languageContext
 * @returns {React.ReactElement} The rendered FormControlSelect component
 */
const FormControlSelect = ({
  name,
  label = name,
  idBase = name,
  options,
  value,
  onChange,
  className = "",
  fullWidth = false,
  displayLabel = true,
  sx,
  languageContext,
  translateOptions = false,
  removeIdFromAriaLabelledby = false,
}) => {
  const normalizedIdBase = String(idBase).trim().replace(/\s+/g, "-");
  const selectId = removeIdFromAriaLabelledby
    ? `${normalizedIdBase}-select`
    : normalizedIdBase;
  const labelId = `${normalizedIdBase}-label`;

  return (
    <FormControl
      className={`filter-select ${className}`}
      fullWidth={fullWidth}
      sx={sx}
    >
      <InputLabel id={labelId} className={!displayLabel ? "sr-only" : ""}>
        {label}
      </InputLabel>
      <Select
        id={selectId}
        name={name}
        value={value || 0}
        onChange={(e) => onChange(e.target.value)}
        className={className}
        displayEmpty
        autoWidth
        sx={sx}
        SelectDisplayProps={{ "aria-labelledby": labelId }}
      >
        {options.map((option) => (
          <MenuItem key={`SctF_${option.value}`} value={option.value}>
            {translateOptions && languageContext
              ? languageContext(option.label)
              : option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default FormControlSelect;
